import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '~/components/buttons/Button/Button';

interface InviteColleagueModalProps {
  open: boolean;
  sending: boolean;
  email: string;
  validationError: string;
  // [MCV-1814] An invitation to this email is already pending; ask whether to resend it.
  pendingExists: boolean;
  resendingPending: boolean;
  onClose: () => void;
  onEmailChange: (email: string) => void;
  onSend: () => void;
  onResendPending: () => void;
  onCancelPending: () => void;
}

export const InviteColleagueModal = ({
  open,
  sending,
  email,
  validationError,
  pendingExists,
  resendingPending,
  onClose,
  onEmailChange,
  onSend,
  onResendPending,
  onCancelPending,
}: InviteColleagueModalProps) => {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            data-cy="employer-colleague-invites-modal"
            className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in data-closed:sm:translate-y-0 data-closed:sm:scale-95"
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 text-content-secondary" />
                <DialogTitle className="text-lg font-semibold text-grey-100">
                  Invite Colleague
                </DialogTitle>
              </div>
              <button
                onClick={onClose}
                className="rounded-full p-1.5 text-grey-400 transition-colors hover:bg-grey-900 hover:text-grey-100"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5 text-content-secondary" />
              </button>
            </div>

            {pendingExists ? (
              <>
                <p
                  data-cy="employer-colleague-invites-pending-exists"
                  className="mb-4 text-sm text-content-primary"
                >
                  {`A pending request already exists for ${email.trim()}. Resend it?`}
                </p>
                <div className="mt-4 flex justify-end gap-3">
                  <Button
                    type="button"
                    colorVariant="secondary"
                    onClick={onCancelPending}
                    disabled={resendingPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    colorVariant="primary"
                    onClick={onResendPending}
                    disabled={resendingPending}
                  >
                    {resendingPending ? 'Resending...' : 'Resend'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <label
                  htmlFor="colleague-invite-email"
                  className="mb-1 block text-sm font-medium text-content-primary"
                >
                  Email Address
                </label>
                <input
                  id="colleague-invite-email"
                  data-cy="employer-colleague-invites-email"
                  type="email"
                  value={email}
                  onChange={(event) => onEmailChange(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && onSend()}
                  placeholder="colleague@example.com"
                  className="mb-1 block w-full rounded-md border border-grey-800 bg-grey-950 p-3 text-sm placeholder:text-grey-700 transition-colors duration-150 focus:border-primary-50 focus:ring-3 focus:ring-primary-50/12 focus:outline-none"
                  disabled={sending}
                />
                {validationError && (
                  <p data-cy="employer-colleague-invites-error" className="mb-3 text-sm text-error">
                    {validationError}
                  </p>
                )}

                <div className="mt-4 flex justify-end gap-3">
                  <Button type="button" colorVariant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="button" colorVariant="primary" onClick={onSend} disabled={sending}>
                    {sending ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </div>
              </>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
